/**
 * Satisfaction feedback (thumbs up/down + optional comment) for assistant
 * responses. Feedback is posted to `POST /v1/feedback`, which attaches it to
 * the LangSmith run that produced the answer (keyed on `message.runId`).
 *
 * The comment modal opens for both thumbs: optional for up, emphasized for
 * down. A single modal instance is driven from this module-scoped state.
 */

import { ref } from 'vue'
import { apiPost } from '@/services/api'
import { useThreads } from './useThreads'
import type { ChatMessage } from '@/types'

const modalOpen = ref(false)
const target = ref<ChatMessage | null>(null)
const sentiment = ref<'up' | 'down' | null>(null)
const submitting = ref(false)
const submitError = ref<string | null>(null)

export function useFeedback() {
  const { activeThreadId } = useThreads()

  function openFeedback(message: ChatMessage, choice: 'up' | 'down') {
    target.value = message
    sentiment.value = choice
    submitError.value = null
    modalOpen.value = true
  }

  function closeFeedback() {
    modalOpen.value = false
    target.value = null
    sentiment.value = null
    submitError.value = null
    submitting.value = false
  }

  async function submitFeedback(comment: string): Promise<void> {
    const message = target.value
    const choice = sentiment.value
    if (!message || !choice) return

    if (!message.runId) {
      // No run id means we can't key feedback in LangSmith. Record the choice
      // locally so the UI reflects it, and close.
      message.feedback = choice
      closeFeedback()
      return
    }

    submitting.value = true
    submitError.value = null
    try {
      await apiPost('/v1/feedback', {
        run_id: message.runId,
        score: choice === 'up' ? 1 : 0,
        comment: comment.trim() || undefined,
        thread_id: activeThreadId.value ?? undefined,
      })
      message.feedback = choice
      closeFeedback()
    } catch (e) {
      submitError.value = e instanceof Error
        ? e.message
        : 'Could not submit feedback. Please try again.'
    } finally {
      submitting.value = false
    }
  }

  return {
    modalOpen,
    target,
    sentiment,
    submitting,
    submitError,
    openFeedback,
    closeFeedback,
    submitFeedback,
  }
}
